import { Link } from 'react-router-dom';
import ArticleLayout from '@/components/shared/ArticleLayout';

const UnderstandingWageTrends = () => (
  <ArticleLayout
    slug="understanding-wage-trends"
    title="Understanding Wage Trends: Growth, Volatility, and What a Recession Does to Your Paycheck"
    description="A wage chart only helps if you know how to read it. Learn the difference between nominal and real wage growth, why employment trends matter more than salary trends, and how different occupations behave when the economy turns."
    category="Market Analysis"
    datePublished="2026-03-10"
    dateModified="2026-06-01"
    readTime="10 min read"
    faqItems={[
      {
        question: 'What is the difference between nominal and real wage growth?',
        answer:
          'Nominal growth is the change in the dollar figure on your pay stub; real growth subtracts inflation. A 4% raise during 7% inflation is a 3% real pay cut. Long-run wage charts that look impressive in nominal terms often flatten considerably once expressed in constant dollars.',
      },
      {
        question: 'Is salary growth or employment growth more important when picking a career?',
        answer:
          'For most people, employment growth matters more. Rising employment means more openings, more employer competition, and more negotiating leverage at every level. A high but stagnant wage in a shrinking occupation is a worse long-term position than a moderate wage in an expanding one.',
      },
      {
        question: 'Which occupations are most recession-resistant?',
        answer:
          'Healthcare, utilities, public safety, education, and repair/maintenance occupations historically hold employment and wages through downturns, because demand for them is not discretionary. Construction, hospitality, recruiting, and advertising-linked roles are typically hit earliest and hardest.',
      },
      {
        question: 'How many years of wage history should I look at?',
        answer:
          'At least ten years, ideally including a recession. Five-year windows can be dominated by a single boom or bust. Wage Atlas trends pages draw on more than two decades of BLS data so you can see how an occupation behaved across multiple economic cycles.',
      },
    ]}
    relatedLinks={[
      {
        title: 'Explore Wage Trends',
        description: 'Interactive growth, volatility, and projection dashboards for any occupation and state.',
        href: '/trends',
      },
      {
        title: 'How to Read Salary Percentiles',
        description: 'Trends tell you where wages are going; percentiles tell you where you stand today.',
        href: '/insights/how-to-read-salary-percentiles',
      },
      {
        title: 'The Salary Negotiation Playbook',
        description: 'Turn trend data into leverage: growth stats are some of the strongest negotiation evidence.',
        href: '/insights/salary-negotiation-guide',
      },
      {
        title: 'Compare Occupations',
        description: 'Put two career paths side by side on growth, wages, and employment.',
        href: '/compare-occupations',
      },
    ]}
  >
    <p>
      Salary data tells you what an occupation pays today. Trend data tells you whether you are
      stepping onto an escalator or a treadmill — and over a thirty-year career, the slope matters
      far more than the starting point. An occupation paying $70,000 with steady 4% growth passes
      one paying $85,000 with 1% growth inside a decade, and keeps pulling away.
    </p>
    <p>
      But trend charts are also the easiest labor-market data to misread. Three distinctions do
      most of the work: nominal versus real growth, wage trends versus employment trends, and
      average growth versus volatility. Get those right and you can read any trends page —
      including ours — like an analyst.
    </p>

    <h2>Nominal vs. real: the inflation trap</h2>
    <p>
      Almost every wage series you will ever see is <strong>nominal</strong> — current dollars,
      unadjusted for inflation. Over short periods that is fine. Over a decade it is dangerously
      flattering: a chart climbing from $60,000 to $78,000 over ten years looks like a 30% raise,
      but if cumulative inflation over the same span was 28%, the occupation's real purchasing
      power barely moved.
    </p>
    <p>
      The practical test is simple: compare the occupation's growth rate to inflation over the same
      window. Occupations that consistently out-grow inflation are gaining real ground — usually a
      sign of persistent labor shortage or rising skill requirements. Occupations that match
      inflation are treading water. Occupations that trail it are quietly getting cheaper, which is
      often the first symptom of automation, offshoring, or an oversupplied pipeline of graduates.
    </p>

    <h2>Wage trends vs. employment trends: read both lines</h2>
    <p>
      A wage chart in isolation can mislead in both directions, because wages respond to supply and
      demand for labor — and you cannot tell from price alone which side is moving. The employment
      line resolves the ambiguity:
    </p>
    <ul>
      <li>
        <strong>Wages up, employment up.</strong> Genuine demand growth. The economy wants more of
        this work and is paying up to get it. The strongest possible configuration.
      </li>
      <li>
        <strong>Wages up, employment down.</strong> Be careful. This pattern often appears when an
        occupation is shrinking from the bottom: automation removes the routine, lower-paid work,
        and the surviving jobs are the harder, better-paid ones. The average wage rises while the
        number of doors falls.
      </li>
      <li>
        <strong>Wages flat, employment up.</strong> Demand is growing but supply keeps pace —
        typical of occupations with fast training pipelines. Plenty of jobs, limited pricing power.
      </li>
      <li>
        <strong>Wages flat or down, employment down.</strong> Structural decline. However the
        present wage looks, the trajectory argues for an exit plan.
      </li>
    </ul>
    <p>
      Every occupation page on our <Link to="/trends">trends explorer</Link> plots both series for
      this reason. The single most valuable habit when researching a career move is to refuse to
      look at one line without the other.
    </p>

    <h2>Volatility: the dimension nobody checks</h2>
    <p>
      Two occupations can share the same ten-year average growth and feel completely different to
      work in. One adds 3% steadily every year; the other swings between +12% booms and -6% busts
      that average to the same number. The second occupation's workers experience layoffs, hiring
      freezes, and feast-or-famine job markets that the average completely conceals.
    </p>
    <p>
      Volatility tends to track how <em>discretionary</em> the underlying demand is. Construction
      wages and employment swing with interest rates and building cycles. Recruiting collapses the
      moment hiring freezes. Advertising-linked roles move with marketing budgets, which are the
      first line cut in a downturn. At the other extreme, dialysis nurses, utility lineworkers, and
      water-treatment operators barely notice recessions, because nobody postpones the services
      they provide.
    </p>
    <p>
      Neither profile is wrong. High-volatility fields often pay a premium in good years precisely
      because of the risk. But the right choice depends on your situation: a worker with savings
      and mobility can harvest boom-year wages; a sole earner with a mortgage may rationally accept
      a lower but stabler wage. The mistake is not knowing which trade you are making.
    </p>

    <h2>What a recession actually does to wages</h2>
    <p>
      Wages are sticky downward — employers cut headcount before they cut pay — so recessions show
      up in the data in a characteristic sequence. Employment falls first and fastest in cyclical
      occupations. Posted wages for <em>new hires</em> soften next, even while incumbent wages hold.
      Then, in the recovery, wage growth concentrates in whichever occupations the rebound favors —
      which is rarely the same set the previous boom favored.
    </p>
    <p>
      This is why a single five-year window can lie about an occupation's character. A window that
      starts at a trough and ends at a peak makes any cyclical field look like a rocket; the
      reverse window makes it look doomed. When you evaluate an occupation on Wage Atlas, pull the
      longest history available and find a recession in it. How deep did employment dip? How many
      years did wages take to regain their real (inflation-adjusted) level? An occupation's worst
      five years tell you more about its risk than its best five tell you about its promise.
    </p>

    <h2>Projections: useful, with a half-life</h2>
    <p>
      Official employment projections extrapolate demographics, industry growth, and technology
      trends about a decade out. They are genuinely informative for structural forces — the aging
      population behind healthcare demand is not going to reverse — and genuinely poor at
      surprises. No published projection anticipated how quickly machine-learning tools would
      change writing-heavy or code-adjacent work, and projections made in 2019 said nothing useful
      about 2020. Treat projections as a prior to be updated by the live data, not a promise. The
      most current signal is always the recent slope of the wage and employment lines themselves.
    </p>

    <h2>A five-question checklist for any trends chart</h2>
    <ol>
      <li><strong>Is this nominal or real?</strong> If nominal, mentally subtract inflation before judging the slope.</li>
      <li><strong>What is employment doing?</strong> Never read the wage line alone.</li>
      <li><strong>How did it behave in the last recession?</strong> Depth of the dip, years to recover.</li>
      <li><strong>Is growth steady or lumpy?</strong> Same average, very different careers.</li>
      <li><strong>Does the local trend match the national one?</strong> State-level trends regularly diverge from the national story — check your actual market with the <Link to="/trends">trends explorer</Link>.</li>
    </ol>
    <p>
      Wage data describes the present; trend data is the closest thing the labor market offers to a
      forecast. Read both, read them together, and read more history than feels necessary. The
      escalator and the treadmill look identical in a snapshot.
    </p>
  </ArticleLayout>
);

export default UnderstandingWageTrends;
